// Extracted from src/pages/staff/VisitorStatistics.js (filterable items
// table) in PR 12 ST 9. Owns local filter state (filterType,
// filterExhibition, filterZone); receives startDate + endDate from the
// orchestrator (shared with ExportControls) and renders the From/To date
// pickers inline with the filters in the section header — matches the
// legacy UI layout. The zone dropdown cascades under the exhibition
// dropdown via ZoneList.exhibitionId.

import { useState, type FC } from "react";

import { useItemStats } from "@shared/api/hooks/useStats";
import { useExhibitions } from "@shared/api/hooks/useExhibitions";
import { useZoneList } from "@shared/api/hooks/useZones";

export interface ItemStatsTableProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
}

const ItemStatsTable: FC<ItemStatsTableProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [filterType, setFilterType] = useState<string>("all");
  const [filterExhibition, setFilterExhibition] = useState<string>("");
  const [filterZone, setFilterZone] = useState<string>("");
  const { data: exhibitions = [] } = useExhibitions();
  const { data: zones = [] } = useZoneList();
  const visibleZones = filterExhibition
    ? zones.filter((z) => z.exhibitionId === filterExhibition)
    : zones;
  const { data: itemStats = [] } = useItemStats({
    start_date: startDate,
    end_date: endDate,
    item_type: filterType === "all" ? null : filterType,
    zone_id: filterZone || null,
    exhibition_id: filterExhibition || null,
  });

  const handleExhibitionChange = (exhibitionId: string) => {
    setFilterExhibition(exhibitionId);
    if (
      filterZone &&
      exhibitionId &&
      !zones.some((z) => z.id === filterZone && z.exhibitionId === exhibitionId)
    ) {
      setFilterZone("");
    }
  };

  return (
    <div className="bg-white border border-border rounded-lg p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold">Exhibition Stats</h2>
          <p className="text-sm text-muted-foreground">
            Activity detail across your exhibitions
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="border border-border rounded-md px-3 py-1.5 text-sm"
              data-testid="item-start-date"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="border border-border rounded-md px-3 py-1.5 text-sm"
              data-testid="item-end-date"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm"
            data-testid="filter-type"
          >
            <option value="all">All Types</option>
            <option value="exhibit">Exhibits Only</option>
            <option value="zone">Zones Only</option>
            <option value="exhibition">Exhibitions Only</option>
          </select>
          <select
            value={filterExhibition}
            onChange={(e) => handleExhibitionChange(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm"
            data-testid="filter-exhibition"
          >
            <option value="">All Exhibitions</option>
            {exhibitions.map((exhibition) => (
              <option key={exhibition.id} value={exhibition.id}>
                {exhibition.titleTc || exhibition.titleEn}
              </option>
            ))}
          </select>
          <select
            value={filterZone}
            onChange={(e) => setFilterZone(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm"
            data-testid="filter-zone"
          >
            <option value="">All Zones</option>
            {visibleZones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.titleTc || zone.titleEn}
              </option>
            ))}
          </select>
        </div>
      </div>

      {itemStats.length === 0 ? (
        <div
          className="text-center py-12 text-muted-foreground"
          data-testid="no-item-stats"
        >
          No data available for the selected filters
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="item-stats-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium">
                  Code
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium">
                  Title
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium">
                  Type
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium">
                  Views
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {itemStats.map((item, index) => (
                <tr key={index} data-testid={`item-row-${index}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-lg font-bold text-primary">
                      {item.item_code}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium">{item.item_title}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.item_type === "exhibit"
                          ? "bg-purple-100 text-purple-700"
                          : item.item_type === "exhibition"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-orange-100 text-orange-700"
                      }`}
                    >
                      {item.item_type === "exhibit"
                        ? "Exhibit"
                        : item.item_type === "exhibition"
                          ? "Exhibition"
                          : "Zone"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-lg font-semibold">
                      {item.view_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ItemStatsTable;
